import React from "react";
import { navigate } from "@reach/router";

import { ReactComponent as IconAgent } from "../assets/images/icon-agent.svg";
import { ReactComponent as IconButton } from "../assets/images/icon-button.svg";
import { ReactComponent as IconWorkflow } from "../assets/images/icon-workflow.svg";
import lpCopyIcon from "../assets/images/lpCopy_icon.svg";
import lpEditIcon from "../assets/images/lpEdit_icon.svg";
import lpPriceUpdateIcon from "../assets/images/lpPriceUpdate_icon.svg";
import lpTemplateIcon from "../assets/images/lpTemplate_icon.svg";

import LoadingState from "./states/LoadingState";
import Config from "../config";
import Session from "./Session";
import Modal from "./modals/Modal";
import classNames from "../utils/classnames";
import fetchFromApi from "../utils/fetchFromApi";

class Tools extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      pageLoading: true,
      loading: true,
      connected: false,
      hubspot: "HubSpot",
      propertyStatus: false,
      propertySuccess: false,
      propertyError: false,
      propertyLoading: false,
      error: false,
    };
  }
  componentDidMount = async () => {
    document.title = `HubSpot ${Config.titleSuffix}`;

    const user = await Session.getUserData();
    if (!user?.org) {
      navigate("/choose-organisation?redirect=/tools");
      return;
    }

    await this.getStatus("lpCopy");
    this.setState({ pageLoading: false });
  };
  async getStatus(appName) {
    try {
      const path = `/connections/hubspot/check/${appName}`;
      const response = await fetchFromApi(path, this.props.location.pathname);
      this.setState({
        [appName]: response,
        pageLoading: false,
      });
    } catch (error) {
      this.setState({ loading: false });
      console.error(error);
    }
  }

  async disconnect(appName) {
    try {
      const path = `/connections/hubspot/disconnect/${appName}`;
      await fetchFromApi(path, this.props.location.pathname);
      this.getStatus(appName);
    } catch (error) {
      console.error(error);
    }
  }

  render() {
    const toolCards = [
      {
        title: "Edit line items",
        badge: "New!",
        icon: lpEditIcon,
        description:
          "Bulk edit multiple line items on a deal. Just choose the property you'd like to update and the new value. New values can be dynamic and you can also choose to only update line items that match certain criteria.",
        documentation:
          "https://www.linepilot.co/articles/edit-line-items-hubspot-deal",
        sources: {
          workflows: true,
          agents: false,
          sidebar: false,
        },
      },
      {
        title: "Copy line items",
        icon: lpCopyIcon,
        description:
          "Automatically copy line items from one deal to another. Perfect for renewals, change requests or templated deals.",
        documentation:
          "https://www.linepilot.co/articles/copy-line-items-between-hubspot-deals",
        sources: {
          workflows: true,
          agents: true,
          sidebar: false,
        },
      },
      {
        title: "Change line item pricing",
        icon: lpPriceUpdateIcon,
        description:
          "Apply a percentage increase or decrease to the the prices of the line items on your deal. Perfect for annual adjustments or applying discounts.",
        documentation:
          "https://www.linepilot.co/articles/change-pricing-your-hubspot-deals",
        sources: {
          workflows: true,
          agents: true,
          sidebar: false,
        },
      },
      {
        title: "Create line items using a template",
        icon: lpTemplateIcon,
        description:
          "Run a LinePilot template to create or update line items on your deal. Perfect for dynamically generating line items, calculating pricing or bundling products.",
        note: "* Running from Workflows requires the Pro plan",
        sources: {
          workflows: true,
          agents: true,
          sidebar: true,
        },
      },
    ];

    const sourceLabels = [
      ["workflows", "Workflows", IconWorkflow],
      ["agents", "Agents", IconAgent],
      ["sidebar", "Deal Sidebar", IconButton],
    ];

    return (
      <>
        {this.state.pageLoading ? (
          <LoadingState />
        ) : (
          <div
            className={classNames(
              this.props?.onboarding ? "my-20" : "w-full my-20",
              "flex flex-col items-center justify-center",
            )}
          >
            <div className="max-w-screen-lg p-16 mx-auto">
              <h1 className="mb-2 text-center text-primary">LinePilot Tools</h1>
              <p className="mt-10 text-center">
                A suite of extra tools that make it easy to build even more
                complex workflows using HubSpot line items. Run tools
                independently of LinePilot templates via workflows, buttons in
                deal sidebars and even through Breeze AI Agents.
              </p>

              <p className="my-10 text-center">
                Tools are included on all pricing plans (except Workflows for
                the Template Tool) - each time a tool is run, it counts towards
                the &quot;Total runs&quot; used each month.
              </p>
              <div className="flex flex-col max-w-3xl gap-4 mx-auto mt-4">
                {toolCards.map((tool, index) => (
                  <div
                    key={index}
                    className="items-start gap-5 p-5 px-6 card hover:ring-primary hover:ring-2 hover:border-transparent"
                  >
                    <img
                      src={tool.icon}
                      alt=""
                      className="flex-shrink-0 w-16 h-16"
                    />
                    <div className="flex-1">
                      <h3 className="flex flex-wrap items-center gap-2">
                        {tool.badge && (
                          <span className="px-2 py-0.5 text-xs font-semibold uppercase rounded-full bg-primary-100 text-primary">
                            {tool.badge}
                          </span>
                        )}
                        {tool.title}
                      </h3>
                      <p className="mt-2 text-sm">{tool.description}</p>
                      {tool.documentation && (
                        <a
                          href={tool.documentation}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex mt-2 text-sm underline"
                        >
                          See Documentation
                        </a>
                      )}
                      {tool.note && (
                        <p className="mt-2 text-xs text-tone-700">
                          {tool.note}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-4 mt-4 text-xs font-semibold text-tone-700">
                        {sourceLabels.map(([key, label, Icon]) => (
                          <div
                            key={key}
                            className={classNames(
                              "flex items-center gap-2",
                              tool.sources[key] ? "" : "opacity-40",
                            )}
                            title={label}
                          >
                            <Icon
                              className={classNames(
                                "w-5 h-5 text-primary",
                                key === "agents" ? "p-0.5" : "p-0",
                              )}
                            />
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Modal
                open={this.state?.error}
                type="error"
                title={this.state.errorDetails?.title}
                message={this.state.errorDetails?.message}
                setClose={() => {
                  this.setState({ error: false }),
                    setTimeout(
                      () => this.setState({ errorDetails: false }),
                      500,
                    );
                }}
              />
            </div>
          </div>
        )}
      </>
    );
  }
}

export default Tools;
